import { Post, TaggedUser } from '@/api/community';
import { PostData } from '@/components/Community/Post';

// Get community base path based on user type
export const getCommunityPath = (userType: string | undefined): string => {
  return userType === 'employer' ? '/employer/community' : '/jobseeker/community';
};

// Helper function to extract hashtags from content
export const extractHashtags = (text: string): string[] => {
  const hashtagRegex = /#(\w+)/g;
  const matches = text.match(hashtagRegex);
  return matches ? matches.map(tag => tag.substring(1)) : [];
};

export const transformAPIPostToUI = (apiPost: Post): PostData => {
  // Transform tagged users (extract just the names if they're objects)
  const taggedUserNames = Array.isArray(apiPost.tagged_users)
    ? apiPost.tagged_users.map((user: string | TaggedUser) =>
        typeof user === 'string' ? user : user.full_name
      )
    : [];

  // Transform shared_post to flat structure if it exists
  let transformedSharedPost = undefined;
  if (apiPost.shared_post && apiPost.shared_post.author) {
    transformedSharedPost = {
      full_name: apiPost.shared_post.author.full_name,
      email: apiPost.shared_post.author.email,
      type: apiPost.shared_post.author.type,
      photo_thumbnail_url: apiPost.shared_post.author.photo_thumbnail_url,
      job_title: apiPost.shared_post.author.job_title,
      id: apiPost.shared_post.author.id,
      content: apiPost.shared_post.content,
      media_urls: apiPost.shared_post.media_urls,
      tagged_users: apiPost.shared_post.tagged_users,
    };
  }

  return {
    ...apiPost,
    tagged_users: taggedUserNames,
    timestamp: new Date(apiPost.created_at),
    isEdited: apiPost.edit_history && apiPost.edit_history.length > 0,
    editedAt: apiPost.edit_history?.[0]?.edited_at
      ? new Date(apiPost.edit_history[0].edited_at)
      : undefined,
    shared_post: transformedSharedPost as any,
  };
};

export interface MediaItem {
  url: string;
  type: 'image' | 'video' | 'other';
}

// Helper function to determine media type from URL
export const mapMediaUrls = (mediaUrls: string[]): MediaItem[] => {
  const getType = (url: string): 'image' | 'video' | 'other' => {
    // Remove query parameters and decode URL
    const decoded = decodeURIComponent(url.split('?')[0]).toLowerCase();

    const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.tiff', '.heic', '.avif'];
    const videoExts = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.m4v', '.3gp'];

    // Check if any image extension appears in the URL path (not just at the end)
    // This handles S3 URLs like: filename.jpg_uuid or filename.png_uuid
    if (imageExts.some(ext => decoded.includes(ext))) return 'image';
    if (videoExts.some(ext => decoded.includes(ext))) return 'video';

    // Fallback: check for 'image' or 'video' keywords in the URL
    if (/image/i.test(url)) return 'image';
    if (/video/i.test(url)) return 'video';

    return 'other';
  };

  return mediaUrls.map(url => ({
    url,
    type: getType(url),
  }));
};
